import _ from "lodash";

export const GROUP_TYPE = {
  B: "b",
  O: "o",
  UM: "um",
  UNKNOWN: "unknown",
};

export const TAB = {
  BO: "bo",
  UM: "um",
  UNKNOWN: "unknown",
};

export const APPLIED_CHART_TYPES = {
  ECPM: "ecpm",
  REVENUE: "revenue",
  IMPRESSION: "impression",
};

export const thClass =
  "px-4 py-2 text-gray-600 text-left text-xs tracking-wider uppercase bg-gray-200";

// { [GROUP_TYPE]: { externalId: { [APPLIED_CHART_TYPES]: number[] }} }
// ex. b: { 1111: { revenue: [], impression: [] }}
export function parseMetaData({ data, dateList }) {
  return _.mapValues(data, (list) => {
    const groupByExternalId = _.groupBy(list, (item) => item.external_id);
    return _.mapValues(groupByExternalId, (items) => {
      const temp = [
        APPLIED_CHART_TYPES.IMPRESSION,
        APPLIED_CHART_TYPES.REVENUE,
      ].map((d) => [d, d]);
      return _.mapValues(_.fromPairs(temp), (type) => {
        return _.map(dateList, (date) => {
          const item = _.find(
            items,
            (item) => item[type] && item.date === date
          );
          return item ? item[type] : 0;
        });
      });
    });
  });
}

// { [GROUP_TYPE]: { [APPLIED_CHART_TYPES]: number[] }}
// ex. b: { revenue: [], impression: [] }
export function getDataByDate({ data }) {
  return _.mapValues(data, (dataObj) => {
    const temp = {};
    _.forEach(
      [APPLIED_CHART_TYPES.REVENUE, APPLIED_CHART_TYPES.IMPRESSION],
      (dataType) => {
        _.set(
          temp,
          [dataType],
          _.zipWith(
            ..._.values(dataObj).map((obj) => obj[dataType]),
            (...nums) => _.sum(nums)
          )
        );
      }
    );

    _.set(
      temp,
      [APPLIED_CHART_TYPES.ECPM],
      _.map(temp[APPLIED_CHART_TYPES.IMPRESSION], (impression, index) => {
        return impression === 0
          ? 0
          : (_.get(temp[APPLIED_CHART_TYPES.REVENUE], index, 0) / impression) *
              1000;
      })
    );

    return temp;
  });
}

// { ...rule, color , data: { [GROUP_TYPE]: { [APPLIED_CHART_TYPES]: { amount, list } }}}[]
// ex. { ...rule, color, data: { b: { revenue: { amount: 0, list: [] }, ... } } }}
export function getDataByExternalId({ data, typeList, transformedRules }) {
  const result = [];
  const externalIdList = _.uniq(
    _.flattenDeep(_.keys(data).map((key) => _.keys(data[key])))
  );

  const revenueAmountByDate = {};
  const impressionAmountByDate = {};

  externalIdList.forEach((exId) => {
    const temp = {};
    _.forEach(typeList, (type) => {
      if (!data[type][exId]) return;
      const revenue = _.get(
        data,
        [type, exId, APPLIED_CHART_TYPES.REVENUE],
        []
      );
      const impression = _.get(
        data,
        [type, exId, APPLIED_CHART_TYPES.IMPRESSION],
        []
      );

      const revenueSum = _.sum(revenue);
      const impressionSum = _.sum(impression);

      if (!revenueAmountByDate[type]) {
        revenueAmountByDate[type] = _.cloneDeep(revenue);
      } else {
        revenueAmountByDate[type] = _.zipWith(
          revenueAmountByDate[type],
          revenue,
          (a, b) => a + b
        );
      }

      if (!impressionAmountByDate[type]) {
        impressionAmountByDate[type] = _.cloneDeep(impression);
      } else {
        impressionAmountByDate[type] = _.zipWith(
          impressionAmountByDate[type],
          impression,
          (a, b) => a + b
        );
      }

      temp[type] = {
        revenue: {
          amount: revenueSum,
          list: revenue,
        },
        impression: {
          amount: impressionSum,
          list: impression,
        },
        ecpm: {
          amount: impressionSum === 0 ? 0 : (revenueSum / impressionSum) * 1000,
          list: _.map(impression, (imp, idx) => {
            return imp === 0 ? 0 : (revenue[idx] / imp) * 1000;
          }),
        },
      };
    });

    result.push({
      externalId: exId,
      data: temp,
    });
  });

  result.forEach((item) => {
    const { data } = item;
    _.keys(data).forEach((type) => {
      const revenueAmount = _.sum(revenueAmountByDate[type]);
      const impressionAmount = _.sum(impressionAmountByDate[type]);

      const revRatio =
        revenueAmount === 0 ? 0 : data[type].revenue.amount / revenueAmount;
      const impRatio =
        impressionAmount === 0
          ? 0
          : data[type].impression.amount / impressionAmount;
      const revRatioByDate = _.zipWith(
        data[type].revenue.list,
        revenueAmountByDate[type],
        (a, b) => (b === 0 ? 0 : a / b)
      );
      const impRatioByDate = _.zipWith(
        data[type].impression.list,
        impressionAmountByDate[type],
        (a, b) => (b === 0 ? 0 : a / b)
      );

      _.set(item, ["data", type, "revenue", "ratio"], revRatio);
      _.set(item, ["data", type, "impression", "ratio"], impRatio);
      _.set(item, ["data", type, "revenue", "ratioByDate"], revRatioByDate);
      _.set(item, ["data", type, "impression", "ratioByDate"], impRatioByDate);
    });
  });

  const parsedTableData = result.map((item) => ({
    ...item,
    ...(_.find(transformedRules, {
      externalId: item.externalId,
    }) || {}),
  }));

  return parsedTableData;
}

export function getAllOptions({ typeList }) {
  const tabOptions = [];
  if (typeList.includes(GROUP_TYPE.B) || typeList.includes(GROUP_TYPE.O)) {
    tabOptions.push({
      value: TAB.BO,
      label: "b vs o",
      groupList: [GROUP_TYPE.B, GROUP_TYPE.O],
    });
  }

  if (typeList.includes(GROUP_TYPE.UM)) {
    tabOptions.push({
      value: TAB.UM,
      label: "um",
      groupList: [GROUP_TYPE.UM],
    });
  }

  if (typeList.includes(GROUP_TYPE.UNKNOWN)) {
    tabOptions.push({
      value: TAB.UNKNOWN,
      label: "unknown",
      groupList: [GROUP_TYPE.UNKNOWN],
    });
  }

  const chartOptions = _.values(APPLIED_CHART_TYPES).map((data) => ({
    value: data,
    label: data,
  }));

  return {
    tabOptions,
    mainChartOptions: _.cloneDeep(chartOptions),
    subChartOptions: _.cloneDeep(chartOptions),
  };
}
